import { useEffect, useState } from "react";
import LocationAPI from "../../axios/locations";

export const MIN_AREA_QUERY = 2;
const DEBOUNCE_MS = 200;

// an area is a city, a part of a city or a location inside one, as returned by the server
export const placeAsArea = (place) => ({
  type: "place",
  id: place.id,
  label: place.label,
  place,
  subplace: null,
  value: null,
});

// where the area is, shown under its name: "Opština Novi Beograd, Beograd"
export const areaContext = (area) =>
  [
    area.type === "location" && area.subplace?.label,
    area.type !== "place" && area.place.label,
  ]
    .filter(Boolean)
    .join(", ");

// the area from the city down, as on its filter chip: "Beograd / Opština Novi Beograd / Blok 70"
export const areaPath = (area) =>
  [
    area.type !== "place" && area.place.label,
    area.type === "location" && area.subplace?.label,
    area.label,
  ]
    .filter(Boolean)
    .join(" / ");

// the search filters after the area is picked; `areas` repeats `subplaces` with the names for display.
// Parts of the same city add up (picking one again removes it), while a whole city or
// a part of another city starts over, because the search covers one city at a time
export const withArea = (area, data) => {
  if (!area.value)
    return { place: area.place, subplaces: [], areas: undefined };
  const current = data.place?.id === area.place.id ? data.areas || [] : [];
  const areas = current.some((a) => a.value === area.value)
    ? current.filter((a) => a.value !== area.value)
    : [...current, area];
  return {
    place: area.place,
    subplaces: areas.map((a) => a.value),
    areas: areas.length > 0 ? areas : undefined,
  };
};

export const clearedAreaFilter = { place: "", subplaces: [], areas: undefined };

export const isAreaSelected = (area, data) =>
  area.value
    ? (data.areas || []).some((a) => a.value === area.value)
    : !data.areas?.length && area.id === data.place?.id;

// "Blok 61, Blok 62 +1": the first names and how many more there are
export const areaSummary = (areas, max) =>
  areas
    .slice(0, max)
    .map((a) => a.label)
    .join(", ") + (areas.length > max ? ` +${areas.length - max}` : "");

// returns the matches and whether they belong to the text as it is now, since
// the answer for the previous keystrokes stays visible until the new one arrives
export const useAreaSuggestions = (query) => {
  const text = query.trim();
  const [result, setResult] = useState({ text: "", areas: [] });

  useEffect(() => {
    if (text.length < MIN_AREA_QUERY) {
      setResult({ text: "", areas: [] });
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      LocationAPI.searchAreas(text)
        .then((res) => !cancelled && setResult({ text, areas: res.data }))
        .catch((err) => console.log(err));
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [text]);

  return [result.areas, result.text === text];
};
